import React, { useState } from 'react';
import { exportToExcel } from '../utils/exportToExcel';
import { usersAPI } from '../services/api';
import useApi from '../hooks/useApi';
import { FaUser, FaEnvelope, FaUserTag, FaClock, FaCalendarAlt, FaEdit, FaTrash, FaBan, FaCheck, FaPlus, FaFileExcel } from 'react-icons/fa';
import './Users.css';

const Users = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user',
    status: 'active'
  });

  // Use API hook for users data
  const { data: users = [], loading, error, refetch } = useApi(usersAPI.getAll);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Update existing user
        await usersAPI.update(editingUser.id, formData);
      } else {
        // Add new user
        await usersAPI.create(formData);
      }
      // Refresh the data
      refetch();
      resetForm();
    } catch (err) {
      console.error('Error saving user:', err);
      // Handle error (show message to user)
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'user',
      status: user.status || 'active'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.delete(id);
        // Refresh the data
        refetch();
      } catch (err) {
        console.error('Error deleting user:', err);
        // Handle error (show message to user)
      }
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      const userToUpdate = users.find(user => user.id === userId);
      if (userToUpdate) {
        await usersAPI.update(userId, { ...userToUpdate, status: newStatus });
        // Refresh the data
        refetch();
      }
    } catch (err) {
      console.error('Error updating user status:', err);
      // Handle error (show message to user)
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: 'user',
      status: 'active'
    });
    setEditingUser(null);
    setShowForm(false);
  };

  // Export function
  const exportUsersToExcel = () => {
    // Prepare data for export
    const exportData = users.map(user => ({
      ID: user.id || '',
      Name: user.name || '',
      Email: user.email || '',
      Role: user.role === 'super_admin' ? 'Super Admin' : 
            user.role === 'admin' ? 'Admin' : 'User',
      Status: user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : '',
      'Last Login': user.last_login || 'Never',
      'Registration Date': user.registration_date || 'N/A'
    }));
    
    exportToExcel(exportData, 'users', 'Users');
  };

  // Handle loading and error states
  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  if (error) {
    return <div className="error">Error loading users: {error.message}</div>;
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <h1><FaUser /> Users Management</h1>
        <div className="header-actions">
          <button 
            className="btn btn-primary" 
            onClick={exportUsersToExcel}
          >
            <FaFileExcel /> Export to Excel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowForm(!showForm)}
          >
            <FaPlus /> {showForm ? 'Cancel' : 'Add New User'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="user-form-container">
          <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
          <form onSubmit={handleSubmit} className="user-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name*</label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address*</label>
                <div className="input-with-icon">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter email address"
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="role">Role*</label>
                <div className="input-with-icon">
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="status">Status*</label>
                <div className="input-with-icon">
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingUser ? 'Update User' : 'Add User'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-list">
        <div className="table-header">
          <h2><FaUser /> Users List</h2>
          <div className="users-count">
            Total Users: {users.length}
          </div>
        </div>
        <div className="table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Registration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.id || ''}</td>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        <FaUser />
                      </div>
                      <div className="user-name">{user.name || ''}</div>
                    </div>
                  </td>
                  <td>
                    <div className="email-info">
                      <FaEnvelope />
                      {user.email || ''}
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge role-${user.role || 'user'}`}>
                      {user.role === 'super_admin' ? 'Super Admin' : 
                       user.role === 'admin' ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.status || 'active'}`}>
                      {user.status ? user.status.charAt(0).toUpperCase() + user.status.slice(1) : 'Active'}
                    </span>
                  </td>
                  <td>
                    <div className="last-login">
                      <FaClock />
                      {user.last_login || 'Never'}
                    </div>
                  </td>
                  <td>
                    <div className="registration-date">
                      <FaCalendarAlt />
                      {user.registration_date || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-small btn-edit"
                        onClick={() => handleEdit(user)}
                        title="Edit User"
                      >
                        <FaEdit />
                      </button>
                      {user.status === 'active' ? (
                        <button 
                          className="btn btn-small btn-delete"
                          onClick={() => handleStatusChange(user.id, 'inactive')}
                          title="Deactivate User"
                        >
                          <FaBan />
                        </button>
                      ) : (
                        <button 
                          className="btn btn-small btn-primary"
                          onClick={() => handleStatusChange(user.id, 'active')}
                          title="Activate User"
                        >
                          <FaCheck />
                        </button>
                      )}
                      <button 
                        className="btn btn-small btn-delete"
                        onClick={() => handleDelete(user.id)}
                        title="Delete User"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {users.length === 0 && (
          <div className="no-users">
            <FaUser />
            <p>No users found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;